import { Component, DestroyRef, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { AddressModel, StepIndicatorModel } from '@app/state/models/add-address.model';
import { sendAccounts, updateCurrentStep } from '../../../state/actions/add-address.action';
import {
  selectAddAddress,
  selectSendAccounts,
  selectStepIndicatorIndex,
} from '../../../state/selectors/add-address.selector';
import { Observable } from 'rxjs';
import { Account } from '@app/models/thirdparty-documents.model';
import { AemUtil } from '@app/utils/aem.util';
import { ThirdpartyDocumentsAemItem } from '@app/models/aem.model';
import {
  ACCOUNTS_AVAILABLE_FOR_SELECTION_TABLE, CANCEL_BUTTON, CONTINUE_BUTTON, INELIGIBLE_ACCOUNT_MESSAGE,
  MAXIMUM_ADDRESSES_ERROR_MESSAGE, NEW_ADDRESS_LABEL, SELECT_ACCOUNT_AND_CONTINUE_ERROR_MESSAGE,
  SELECT_ACCOUNT_TABLE_LABEL, SELECT_ACCOUNTS_FUNDS_PAGE_CONTAINER, SHOW_LESS_LABEL, SHOW_MORE_LABEL,
} from '@app/constants/aem.constant';

@Component({
  selector: 'app-select-accounts-funds',
  templateUrl: './select-accounts-funds.component.html',
  styleUrls: ['./select-accounts-funds.component.scss'],
  standalone: false,
})
export class SelectAccountsFundsComponent implements OnInit {
  labels = {
    heading: '',
    subheading: '',
    errorMessage: '',
    symbol: '',
    name: '',
    fundAccount: '',
    newAddress: '',
    titleText: '',
    accountsLabel: '',
    countinueLabel: '',
    cancelLabel: '',
    showMore: '',
    showLess: '',
    selectAccountTableLabel: '',
    ineligibleAccountMessage: 'This account is ineligible to be selected for duplicate documents',
    maximumAddressesErrorMessage: 'You have reached the maximum number of accounts for this address.',
  };
  showMaxAddressesError = false;
  isUpdateFlow = false;
  currentStepIndicatorIndex$: Observable<number>;
  address$: Observable<AddressModel>;
  allAccounts$: Observable<Account[]>;
  accounts: any;
  associatedAccounts: any;
  showSelectAccontErrorBanner: boolean = false;
  @Input() thirdpartyDocumentsAemContent: ThirdpartyDocumentsAemItem;
  @ViewChild('fundErrorBannner') fundErrorBannner: ElementRef;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private _router: Router,
    private store: Store<StepIndicatorModel>,
  ) {
  }

  ngOnInit(): void {
    this.currentStepIndicatorIndex$ = this.store.pipe(select(selectStepIndicatorIndex));
    this.address$ = this.store.pipe(select(selectAddAddress));
    this.address$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.associatedAccounts = data.associatedAccounts;
      this.isUpdateFlow = (data.associatedAccounts?.length ?? 0) > 0;
    });
    this.allAccounts$ = this.store.pipe(select(selectSendAccounts));
    this.allAccounts$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.accounts = JSON.parse(JSON.stringify(data.accounts));
      this.accounts.forEach((account: any) => {
        let positionCounter = 0;
        this.associatedAccounts.forEach((assocAccount: any) => {
          if (account.productType.includes('MUTUAL_FUND')) {
            account.positions.forEach((position: any) => {
              if (position.vastAccountNumber === assocAccount.accountId) {
                position['isChecked'] = true;
                position['isOriginallyLinked'] = true;
                position['optedFor'] = assocAccount.interestedPartyDocumentPreference || '';
                positionCounter++;
              }
            });
            if (positionCounter > 0 && positionCounter === account.positions.length) {
              account['isChecked'] = true;
            }
          }
          if (account.productType.includes('BROKERAGE') && account.accountId === assocAccount.accountId) {
            account['isChecked'] = true;
            account['isOriginallyLinked'] = true;
            account['optedFor'] = assocAccount.interestedPartyDocumentPreference || '';
          }
        });
        if (account.productType.includes('MUTUAL_FUND')) {
          account['isOriginallyLinked'] = account.positions.some((p: any) => p.isOriginallyLinked);
        }
      });
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.getAemContent();
  }

  private getAemContent(): void {
    const container = AemUtil.getChildren(SELECT_ACCOUNTS_FUNDS_PAGE_CONTAINER, this.thirdpartyDocumentsAemContent?.children);
    this.labels.accountsLabel = container?.subheading ?? '';
    this.labels.titleText = container?.body ? AemUtil.getHtmlBody(container.body) : '';
    this.labels.heading = AemUtil.getPageContainerHeading(this.thirdpartyDocumentsAemContent, SELECT_ACCOUNTS_FUNDS_PAGE_CONTAINER);
    this.labels.subheading = container?.subheading ?? '';
    const message = container?.messages?.find(m => m._path?.includes(SELECT_ACCOUNT_AND_CONTINUE_ERROR_MESSAGE));
    this.labels.errorMessage = message?.body ? AemUtil.getHtmlBody(message.body) : '';
    const ineligibleMessage = container?.messages?.find(m => m._path?.includes(INELIGIBLE_ACCOUNT_MESSAGE));
    if (ineligibleMessage?.body) this.labels.ineligibleAccountMessage = AemUtil.getHtmlTextFromBodyChild(ineligibleMessage.body);
    const maxAddressesMessage = container?.messages?.find(m => m._path?.includes(MAXIMUM_ADDRESSES_ERROR_MESSAGE));
    if (maxAddressesMessage?.body) this.labels.maximumAddressesErrorMessage = AemUtil.getHtmlTextFromBodyChild(maxAddressesMessage.body);
    const selectionPageFields = AemUtil.getTableColumnLabels(this.thirdpartyDocumentsAemContent, ACCOUNTS_AVAILABLE_FOR_SELECTION_TABLE);
    this.labels.symbol = selectionPageFields.symbol;
    this.labels.name = selectionPageFields.name;
    this.labels.fundAccount = selectionPageFields.fundAccount;
    this.labels.newAddress = AemUtil.getLabelTextFromLabels(this.thirdpartyDocumentsAemContent, NEW_ADDRESS_LABEL);
    this.labels.countinueLabel = AemUtil.getPrimaryCtaLabel(this.thirdpartyDocumentsAemContent, CONTINUE_BUTTON);
    this.labels.cancelLabel = AemUtil.getPrimaryCtaLabel(this.thirdpartyDocumentsAemContent, CANCEL_BUTTON);
    this.labels.showMore = AemUtil.getLabelTextFromLabels(this.thirdpartyDocumentsAemContent, SHOW_MORE_LABEL);
    this.labels.showLess = AemUtil.getLabelTextFromLabels(this.thirdpartyDocumentsAemContent, SHOW_LESS_LABEL);
    this.labels.selectAccountTableLabel = AemUtil.getLabelTextFromLabels(this.thirdpartyDocumentsAemContent, SELECT_ACCOUNT_TABLE_LABEL);
  }

  goToPage(route?: string) {
    if (route) {
      this.store.dispatch(
        sendAccounts({
          accounts: {
            accounts: [{
              accountId: '',
              accountName: '',
              brokerageAccountNumber: '',
              productType: '',
              noOfLinkedAddresses: 0,
              positions: [
                {
                  fundName: '',
                  symbol: '',
                  vanguardFundId: '',
                  vastAccountNumber: '',
                  noOfLinkedAddresses: 0,
                },
              ],
            }],
          },
        }),
      );
      this._router.navigate([route]);
    } else {
      let counter: number = 0;
      this.accounts.forEach((element: any) => {
        if (element.productType.includes('BROKERAGE') && element.isChecked) {
          counter++;
        }
        if (element.productType.includes('MUTUAL_FUND')) {
          element.positions.forEach((el: any) => {
            if (el.isChecked) {
              counter++;
            }
          });
        }
      });
      if (!counter) {
        this.showSelectAccontErrorBanner = true;
        this.showMaxAddressesError = false;
        this.fundErrorBannner.nativeElement.scrollIntoView({ behavior: 'smooth' });
      } else {
        this.store.dispatch(
          updateCurrentStep({ currentStepIndex: 3 }),
        );
        this.store.dispatch(
          sendAccounts({ accounts: { accounts: this.accounts } }),
        );
      }
    }
  }

  handleClick = (event: any) => {
    const expandedState = event.currentTarget.getAttribute('aria-expanded') === 'true';
    event.currentTarget.setAttribute('title', expandedState ? this.labels.showMore : this.labels.showLess);
    event.currentTarget.setAttribute('aria-expanded', String(!expandedState));
    if (!expandedState) {
      event.currentTarget.parentElement.parentElement.classList.add('c11n-accordion--expanded');
    } else {
      event.currentTarget.parentElement.parentElement.classList.remove('c11n-accordion--expanded');
    }
  };

  headerCheckBox(accountData: any, selection: any) {
    this.showSelectAccontErrorBanner = false;
    this.showMaxAddressesError = false;
    if (!accountData.brokerageAccountNumber) {
      accountData.positions.forEach((el: any) => {
        el.isChecked = selection.target.checked;
      });
    } else {
      accountData.isChecked = selection.target.checked;
    }
  }

  mutualFundCheckbox(mutualFundData: any, mainAccountData: any, selection: any) {
    this.showSelectAccontErrorBanner = false;
    this.showMaxAddressesError = false;
    mutualFundData.isChecked = selection.target.checked;
    mainAccountData.isChecked = mainAccountData.positions.length === mainAccountData.positions.filter((el: any) => el.isChecked).length;
  }

  sortColumn(accountData: any, columnSelection: any) {
    accountData.activeSort = columnSelection;
    if (accountData.sortingState === 'descending') {
      this.sortAscending(accountData.positions, columnSelection);
      accountData.sortingState = 'ascending';
    } else {
      this.sortDescending(accountData.positions, columnSelection);
      accountData.sortingState = 'descending';
    }
  }

  trackByAccountId(_index: number, account: any): string {
    return account.accountId;
  }

  sortDescending(arr: any[], column: string) {
    return arr.sort((a: any, b: any) => (b[column] ?? '').toString().localeCompare((a[column] ?? '').toString()));
  }

  sortAscending(arr: any[], column: string) {
    return arr.sort((a: any, b: any) => (a[column] ?? '').toString().localeCompare((b[column] ?? '').toString()));
  }
}
